import { Box } from "@mui/material";
import { useEffect } from "react";

interface EventPinProps {
  /** Icon URL or SVG path string (starting with "<path" or "<") */
  iconUrl?: string;
  /** Pin color (hex code) */
  pinHue?: string;
  /** Size variant: 'small' (24px), 'medium' (28px), 'large' (28x36px for map), or custom object */
  size?: "small" | "medium" | "large" | { width: string; height: string };
  /** Whether to use MUI Box (true) or plain div (false). Default: true */
  useMuiBox?: boolean;
  /** Additional CSS styles (for MUI Box) */
  sx?: any;
  /** Additional inline styles (for non-MUI contexts) */
  style?: React.CSSProperties;
  /** Click handler */
  onClick?: () => void;
}

/**
 * Reusable Event Pin component that renders a pin icon with color and optional icon overlay.
 * Supports both MUI Box and plain div contexts, with responsive sizing options.
 */
export const EventPin = ({
  iconUrl,
  pinHue = "#FF0000",
  size = "medium",
  useMuiBox = true,
  sx,
  style,
  onClick,
}: EventPinProps) => {
  // Reset state if icon changes
  useEffect(() => { }, [iconUrl]);

  const isSvgIcon = iconUrl && (iconUrl.startsWith("<path") || iconUrl.startsWith("<"));

  // For EventMap context (large size, plain div, rectangular 28x36)
  if (!useMuiBox) {
    const containerStyle: React.CSSProperties = {
      position: "relative",
      width: typeof size === "object" ? size.width : size === "large" ? "28px" : "28px",
      height: typeof size === "object" ? size.height : size === "large" ? "36px" : "28px",
      cursor: onClick ? "pointer" : "default",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      ...style,
    };

    const svgWidth = typeof size === "object" ? size.width : size === "large" ? "28px" : "28px";
    const svgHeight = typeof size === "object" ? size.height : size === "large" ? "36px" : "36px";
    const iconCircleSize = size === "large" ? "18px" : "10px";
    const iconSize = size === "large" ? "14px" : "11px";
    const iconTop = size === "large" ? "5px" : "6px";

    return (
      <div style={containerStyle} onClick={onClick}>
        <svg
          width={svgWidth}
          height={svgHeight}
          viewBox="0 0 28 36"
          style={{
            position: "relative",
            zIndex: 1,
            overflow: "visible",
            width: svgWidth,
            height: svgHeight,
          }}
        >
          <path
            d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 22 14 22s14-11.5 14-22c0-7.73-6.27-14-14-14z"
            fill={pinHue}
            stroke="#fff"
            strokeWidth="2"
          />
        </svg>

        <div
          style={{
            position: "absolute",
            top: iconTop,
            left: "50%",
            transform: "translateX(-50%)",
            width: iconCircleSize,
            height: iconCircleSize,
            backgroundColor: "#fff",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            overflow: "hidden",
          }}
        >
          {iconUrl ? (
            isSvgIcon ? (
              <div
                style={{
                  width: iconSize,
                  height: iconSize,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: pinHue,
                  transform: "translateY(0.5px)",
                }}
                dangerouslySetInnerHTML={{
                  __html: `<svg xmlns="http://www.w3.org/2000/svg" width="${iconSize}" height="${iconSize}" 
                    viewBox="0 0 24 24" fill="${pinHue}" stroke="none">${iconUrl}</svg>`,
                }}
              />
            ) : (
              <img
                src={iconUrl}
                alt=""
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  borderRadius: "50%",
                }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            )
          ) : (
            <div
              style={{
                width: iconSize,
                height: iconSize,
                backgroundColor: "#fff",
                borderRadius: "50%",
              }}
            />
          )}
        </div>
      </div>
    );
  }

  // For MUI Box contexts (cards, list items, etc.)
  const containerWidth = typeof size === "object"
    ? size.width
    : size === "small"
      ? { xs: "24px", sm: "24px", md: "24px" }
      : size === "large"
        ? "28px"
        : { xs: "24px", sm: "28px", md: "28px" };

  const containerHeight = typeof size === "object"
    ? size.height
    : size === "small"
      ? { xs: "24px", sm: "24px", md: "24px" }
      : size === "large"
        ? "28px"
        : { xs: "24px", sm: "28px", md: "28px" };

  const svgWidth = typeof size === "object"
    ? "100%"
    : size === "small"
      ? { xs: "16px", sm: "16px", md: "16px" }
      : { xs: "16px", sm: "18px", md: "18px" };

  const svgHeight = typeof size === "object"
    ? "100%"
    : size === "small"
      ? { xs: "20px", sm: "20px", md: "20px" }
      : { xs: "20px", sm: "22px", md: "22px" };

  const iconCircleWidth = typeof size === "object"
    ? "10px"
    : size === "small"
      ? { xs: "8px", sm: "8px", md: "8px" }
      : { xs: "8px", sm: "10px", md: "10px" };

  const iconCircleHeight = typeof size === "object"
    ? "10px"
    : size === "small"
      ? { xs: "8px", sm: "8px", md: "8px" }
      : { xs: "8px", sm: "10px", md: "10px" };

  const iconInnerSize = typeof size === "object"
    ? "11px"
    : size === "small"
      ? { xs: "9px", sm: "9px", md: "9px" }
      : { xs: "9px", sm: "11px", md: "11px" };

  const iconTop = typeof size === "object"
    ? "6px"
    : size === "small"
      ? { xs: "5px", sm: "5px", md: "5px" }
      : { xs: "5px", sm: "6px", md: "6px" };

  return (
    <Box
      sx={{
        position: "relative",
        width: containerWidth,
        height: containerHeight,
        cursor: onClick ? "pointer" : "default",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "white",
        borderRadius: "50%",
        padding: size === "small" ? "2px" : size === "large" ? "3px" : { xs: "2px", sm: "3px", md: "3px" },
        ...sx,
      }}
      onClick={onClick}
    >
      <Box
        sx={{
          position: "relative",
          width: svgWidth,
          height: svgHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <svg
          width="18"
          height="22"
          viewBox="0 0 28 36"
          style={{
            position: "relative",
            zIndex: 1,
            overflow: "visible",
            width: "100%",
            height: "100%",
          }}
        >
          <path
            d="M14 0C6.27 0 0 6.27 0 14c0 10.5 14 22 14 22s14-11.5 14-22c0-7.73-6.27-14-14-14z"
            fill={pinHue}
            stroke="#fff"
            strokeWidth="2"
          />
        </svg>
      </Box>
      <Box
        sx={{
          position: "absolute",
          top: iconTop,
          left: "50%",
          transform: "translateX(-50%)",
          width: iconCircleWidth,
          height: iconCircleHeight,
          backgroundColor: "#fff",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2,
          overflow: "hidden",
        }}
      >
        {iconUrl ? (
          isSvgIcon ? (
            <Box
              sx={{
                width: iconInnerSize,
                height: iconInnerSize,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: pinHue,
                transform: "translateY(0.5px)",
              }}
              dangerouslySetInnerHTML={{
                __html: `<svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" 
                  viewBox="0 0 24 24" fill="${pinHue}" stroke="none">${iconUrl}</svg>`,
              }}
            />
          ) : (
            <img
              src={iconUrl}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                borderRadius: "50%",
              }}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          )
        ) : (
          <Box
            sx={{
              width: iconInnerSize,
              height: iconInnerSize,
              backgroundColor: "#fff",
              borderRadius: "50%",
            }}
          />
        )}
      </Box>
    </Box>
  );
};

